// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Alarm } from '../../../../types/alarm';
import { alarmTitle } from '../../../../utils/powerFlowAlarms';
import { alarmDetailPath } from '../../../../services/alarmSelectors';

/** Which breaker's ALARM mark was activated, and where it sits on the stage. */
export interface BreakerAlarmTarget {
  ids: string[];
  x: number;
  y: number;
  horizontal?: boolean;
}

export interface BreakerAlarmPopoverProps {
  target: BreakerAlarmTarget;
  /** The alarm service's open alarms on the breaker. */
  alarms: Alarm[];
  nameOf: (_id: string) => string;
  onClose: () => void;
}

/**
 * What the breaker's ALARM mark stands for, one entry per open alarm, each
 * linking to the alarm detail page the Home list opens. The stage mounts it
 * only while that list is non-empty: a poll that closes the last alarm takes
 * the popover away with the mark.
 *
 * Escape and a press anywhere else close it, as the notices popover does.
 */
export const BreakerAlarmPopover = ({
  target,
  alarms,
  nameOf,
  onClose,
}: BreakerAlarmPopoverProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [onClose]);

  const title = `${target.ids.map(nameOf).join(', ')} Alarms (${alarms.length})`;
  return (
    <div
      ref={rootRef}
      className="sld-alarm-popover"
      role="dialog"
      aria-label={title}
      // Beside the ALARM word on a vertical run, below the glyph on a horizontal one.
      style={
        target.horizontal
          ? { left: target.x - 40, top: target.y + 28 }
          : { left: target.x + 64, top: target.y - 12 }
      }
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      data-testid="breaker-alarm-popover"
    >
      <p className="sld-alarm-popover__title">
        <span>{title}</span>
        <button
          type="button"
          className="sld-alarm-popover__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </p>
      <ul>
        {alarms.map(alarm => (
          <li key={alarm.uid}>
            <Link to={alarmDetailPath(alarm)}>{alarmTitle(alarm) ?? alarm.id}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
